<!--
  Actions 组件 - 头部右侧操作区
  包含：API入口、通知中心、批量任务状态、用户菜单
-->
<template>
  <div class="ah-right">
    <!-- API标识 -->
    <ApiBadge @click="handleOpenApiSite" />

    <div class="ah-controls">
      <!-- 通知铃铛 -->
      <NotificationBell />

      <!-- 用户菜单 -->
      <UserMenu
        :is-logged-in="isLoggedIn"
        :user-info="userInfo"
        :quota-used="getCurrentQuotaUsed"
        :quota-limit="getCurrentQuotaLimit"
        :default-avatar="defaultAvatar"
        :is-dark-theme="isDarkTheme"
        @logout="$emit('logout')"
        @go-to-profile="handleGoToProfile"
        @theme-toggle="$emit('theme-toggle')"
        @show-guide="handleShowGuide"
        @open-api-site="$emit('open-api-site')"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useUserStore } from '@/store/user'
import { useGuideStore } from '@/store/guide'
import { useTheme } from '@/composables/useTheme'
import { useTTSConfiguration } from '@/composables/useTTSConfiguration'
import ApiBadge from '@/components/ui/ApiBadge.vue'
import UserMenu from '@/components/ui/UserMenu.vue'
import NotificationBell from '@/components/common/NotificationBell.vue'
import { trackRumClick } from '@/utils/rum'

// 事件定义
const emit = defineEmits<{
  'theme-toggle': []
  'open-api-site': []
  'logout': []
}>()

// Store 和 Composables
const router = useRouter()
const userStore = useUserStore()
const guideStore = useGuideStore()
const theme = useTheme()
const ttsConfig = useTTSConfiguration()
const { isLoggedIn, currentUser } = storeToRefs(userStore)
const { getCurrentQuotaUsed: _quotaUsed, getCurrentQuotaLimit: _quotaLimit } = ttsConfig

// 响应式计算属性
const isDarkTheme = computed(() => theme.isDarkTheme.value)
const getCurrentQuotaUsed = computed(() => _quotaUsed.value)
const getCurrentQuotaLimit = computed(() => _quotaLimit.value)

// 用户信息
const userInfo = computed(() => {
  if (!currentUser.value) return undefined
  return {
    username: currentUser.value.username,
    avatar: currentUser.value.avatar,
    role: currentUser.value.role
  }
})

// 默认头像
const defaultAvatar = computed(() =>
  `https://api.dicebear.com/7.x/micah/svg?seed=${currentUser.value?.username || 'default'}&backgroundColor=b6e3f4`
)

// 动作处理
const handleShowGuide = async () => {
  trackRumClick('top_bar', 'show_guide')
  await guideStore.startGuide()
}

const handleGoToProfile = () => {
  trackRumClick('top_bar', 'go_to_profile', {
    user_id: currentUser.value?.id,
    username: currentUser.value?.username
  })
  router.push('/user/overview')
}

const handleOpenApiSite = () => {
  trackRumClick('top_bar', 'open_api_site')
  emit('open-api-site')
}
</script>

<style scoped>
.ah-right {
  display: flex;
  align-items: center;
  gap: 18px;
  flex-shrink: 0;
}

.ah-controls {
  display: flex;
  align-items: center;
  gap: 14px;
}
</style>
